import React, { useState, useRef, useEffect } from "react";
import "./bulkupload.css";
import { McFileUpload } from "@maersk-global/mds-react-wrapper/components-core/mc-file-upload";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-select";
import { McOption } from "@maersk-global/mds-react-wrapper/components-core/mc-option";
import { McInputDate } from "@maersk-global/mds-react-wrapper/components-core/mc-input-date";
import { saveAs } from "file-saver";

// ── Constants ─────────────────────────────────────────────────────
const BASE = `${process.env.REACT_APP_API_BASE_URL}/bulk-approval`;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const STALE_MINUTES = 15;
const POLL_MS = 5000;
const POLL_CEILING_MS = STALE_MINUTES * 60 * 1000;
const HISTORY_PAGE_SIZE = 20;
const RECORDS_PAGE_SIZE = 50;

const MODULES = {
    event: {
        label: "Event",
        columns: "Parent ID, Child ID, Action, Comments",
        from: "Awaiting Customer Response",
        approve: "Event Charges Approved",
        reject: "Event Charges Rejected"
    },
    cost: {
        label: "Cost",
        columns: "Parent ID, Child ID, Amount, Currency, Action, Comments",
        from: "Event Charges Approved",
        approve: "Cost Charges Approved",
        reject: "Cost Charges Rejected"
    }
};

const REJECT_REASONS = [
    "Incorrect approver",
    "Missing supporting documents, required details, incorrect information",
    "Service is not accepted",
    "Duplicate",
    "Incorrect rate",
    "Incorrect quantity",
    "To be billed to supplier/shipper",
    "Late request"
];

const ROW_TAGS = {
    valid:   { label: "Valid",   appearance: "success" },
    failed:  { label: "Failed",  appearance: "error" },
    skipped: { label: "Skipped", appearance: "warning" }
};
const RECORD_TAGS = {
    Success: { label: "Success", appearance: "success" },
    Failed:  { label: "Failed",  appearance: "error" },
    Skipped: { label: "Skipped", appearance: "warning" }
};
const TX_TAGS = {
    "Completed":   { label: "Completed",   appearance: "success" },
    "Failed":      { label: "Failed",      appearance: "error" },
    "Interrupted": { label: "Interrupted", appearance: "warning" },
    "In Progress": { label: "In Progress", appearance: "info" }
};
const PILL_CLASS = { success: "ok", error: "err", warning: "warn", info: "info", neutral: "neutral" };

const tagFor = (map, value) => map[value] || { label: value || "—", appearance: "neutral" };

// ── Helpers ───────────────────────────────────────────────────────
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getToken = (token) => token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);

const authHeaders = (token) => ({
    "Authorization": `Bearer ${getToken(token)}`,
    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
});

const buildQuery = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    return qs.toString();
};

// Backend uses both { error } and { detail } (string or FastAPI validation array)
async function readError(res) {
    let body = {};
    try { body = await res.json(); } catch { /* non-JSON body */ }
    const detail = Array.isArray(body.detail)
        ? body.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : body.detail;
    return {
        status: res.status,
        message: body.error || detail || `Request failed (HTTP ${res.status})`,
        errors: Array.isArray(body.errors) ? body.errors : [],
        hasDetail: Boolean(body.detail),
        body
    };
}

// Timestamps from the API are UTC without a timezone suffix
const parseUtc = (ts) => {
    if (!ts) return null;
    const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(ts);
    const d = new Date(hasZone ? ts : `${ts}Z`);
    return isNaN(d) ? null : d;
};
const formatUtc = (ts) => {
    const d = parseUtc(ts);
    return d ? d.toLocaleString() : (ts || "—");
};
const isStale = (tx) => {
    if (tx.status !== "In Progress") return false;
    const started = parseUtc(tx.started_at);
    return Boolean(started) && Date.now() - started.getTime() > STALE_MINUTES * 60 * 1000;
};

async function downloadFile(token, params, fallbackName) {
    const res = await fetch(`${BASE}/download?${buildQuery(params)}`, { headers: authHeaders(token) });
    if (!res.ok) throw await readError(res);
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    saveAs(blob, match ? decodeURIComponent(match[1]) : fallbackName);
}

function useDownloader(token, toastRef) {
    const [busy, setBusy] = useState("");
    const download = async (key, params, fallbackName) => {
        if (busy) return;
        setBusy(key);
        try {
            await downloadFile(token, params, fallbackName);
        } catch (e) {
            toastRef.current(e.message || "Download failed", "error");
        } finally {
            setBusy("");
        }
    };
    return { download, busy };
}

function parseFrame(frame) {
    let event = "message";
    const data = [];
    for (const line of frame.split("\n")) {
        if (!line || line.startsWith(":")) continue; // blank / keepalive comment
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    }
    if (!data.length) return null;
    try { return { event, data: JSON.parse(data.join("\n")) }; } catch { return null; }
}

// ── Small presentational pieces ───────────────────────────────────
function DataTable({ columns, rows, tableKey }) {
    const cols = columns.map(({ tags, ...col }) => ({
        ...col,
        cellTemplate: tags
            ? (e) => {
                const t = tagFor(tags, e.rowData[col.id]);
                return e.html`<mc-tag label="${t.label}" appearance="${t.appearance}"></mc-tag>`;
            }
            : undefined
    }));
    return (
        <div className={`bulk-table-frame${rows.length > 10 ? " bulk-table-frame--scroll" : ""}`}>
            <div className="bulk-table-scroll">
                <McTable
                    key={tableKey}
                    data={rows}
                    datakey="id"
                    columns={cols}
                    zebrastripes
                    headersticky
                    fit="small"
                    height="auto"
                    recordsperpage={rows.length || 25}
                    style={{ border: "none" }}
                />
            </div>
        </div>
    );
}

function Pill({ map, value }) {
    const t = tagFor(map, value);
    return <span className={`bulk-pill bulk-pill--${PILL_CLASS[t.appearance] || "neutral"}`}>{t.label}</span>;
}

function SummaryCard({ tone, value, label }) {
    return (
        <div className={`bulk-summary-card bulk-summary-${tone}`}>
            <div className="bulk-summary-value">{value}</div>
            <div className="bulk-summary-label">{label}</div>
        </div>
    );
}

function Pager({ page, pageSize, total, onChange }) {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    if (total <= pageSize) return null;
    return (
        <div className="bulk-pager">
            <McButton label="Previous" appearance="secondary" fit="small" disabled={page <= 1} onClick={() => onChange(page - 1)} />
            <span className="bulk-pager-info">Page {page} of {pages} · {total} records</span>
            <McButton label="Next" appearance="secondary" fit="small" disabled={page >= pages} onClick={() => onChange(page + 1)} />
        </div>
    );
}

function ModuleToggle({ value, onChange, disabled }) {
    return (
        <div className="bulk-segment" role="tablist">
            {Object.entries(MODULES).map(([id, m]) => (
                <button
                    key={id}
                    type="button"
                    disabled={disabled}
                    className={`bulk-segment-btn${value === id ? " bulk-segment-btn--active" : ""}`}
                    onClick={() => onChange(id)}
                >
                    {m.label}
                </button>
            ))}
        </div>
    );
}

function RulesList({ moduleId }) {
    const m = MODULES[moduleId];
    return (
        <details className="bulk-rules">
            <summary>Upload rules</summary>
            <ul>
                <li><strong>.xlsx</strong> only, max <strong>5 MB</strong> and <strong>1000 records</strong>. Headers on row 1 from column A.</li>
                <li>Columns: <strong>{m.columns}</strong></li>
                <li>Action must be <strong>APPROVE</strong> or <strong>REJECT</strong>. Comments for APPROVE are free text.</li>
                <li>
                    Comments are mandatory for REJECT and must be exactly one of these reasons (case and extra spaces are ignored).
                    The template has a Comments dropdown fed from its <strong>Reject Reasons</strong> sheet.
                    <ol className="bulk-reasons">
                        {REJECT_REASONS.map((r) => <li key={r}>{r}</li>)}
                    </ol>
                </li>
                <li>Charge must be <strong>{m.from}</strong>. Approve gives <strong>{m.approve}</strong>, reject gives <strong>{m.reject}</strong>.</li>
                {moduleId === "cost" && <li>Amount and Currency are only checked against the charge, they are not changed.</li>}
                <li>A row fails if an ID is blank or unknown, the charge is Invalid, a PO or invoice already exists, the status is not eligible, or the Child ID is duplicated in the file.</li>
                <li>A row is skipped if the charge is already in the target status.</li>
                <li>A failed row never blocks other rows. A validated upload stays valid for 24 hours and can be confirmed once.</li>
            </ul>
        </details>
    );
}

// ── History ───────────────────────────────────────────────────────
function ApprovalHistory({ token, toastRef }) {
    const [draft, setDraft]       = useState({ module: "", date_from: "", date_to: "" });
    const [applied, setApplied]   = useState({ module: "", date_from: "", date_to: "" });
    const [page, setPage]         = useState(1);
    const [list, setList]         = useState({ data: [], total: 0 });
    const [loading, setLoading]   = useState(false);
    const [reloadTick, setReload] = useState(0);

    const [selected, setSelected]     = useState(null);
    const [recPage, setRecPage]       = useState(1);
    const [records, setRecords]       = useState({ data: [], total: 0 });
    const [recLoading, setRecLoading] = useState(false);

    const { download, busy } = useDownloader(token, toastRef);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const qs = buildQuery({ ...applied, page, page_size: HISTORY_PAGE_SIZE });
                const res = await fetch(`${BASE}/history?${qs}`, { headers: authHeaders(token) });
                if (!res.ok) throw await readError(res);
                const body = await res.json();
                if (!cancelled) setList({ data: body.data || [], total: body.total || 0 });
            } catch (e) {
                if (!cancelled) toastRef.current(e.message || "Could not load history", "error");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applied, page, token, reloadTick, toastRef]);

    const selectedId = selected?.bulk_transaction_id;
    useEffect(() => {
        if (!selectedId) return undefined;
        let cancelled = false;
        (async () => {
            setRecLoading(true);
            try {
                const qs = buildQuery({ bulk_transaction_id: selectedId, page: recPage, page_size: RECORDS_PAGE_SIZE });
                const res = await fetch(`${BASE}/history?${qs}`, { headers: authHeaders(token) });
                if (!res.ok) throw await readError(res);
                const body = await res.json();
                if (!cancelled) setRecords({ data: body.data || [], total: body.total || 0 });
            } catch (e) {
                if (!cancelled) toastRef.current(e.message || "Could not load records", "error");
            } finally {
                if (!cancelled) setRecLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedId, recPage, token, toastRef]);

    // Only keep complete YYYY-MM-DD values (or empty) so half-typed dates never reach the API
    const handleDateInput = (field, value) => {
        const v = (value || "").trim();
        if (v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v)) setDraft((d) => ({ ...d, [field]: v }));
    };

    const applyFilters = () => {
        if (draft.date_from && draft.date_to && draft.date_from > draft.date_to) {
            toastRef.current("'From' date cannot be after 'To' date", "error");
            return;
        }
        setPage(1);
        setApplied(draft);
    };
    const resetFilters = () => {
        const empty = { module: "", date_from: "", date_to: "" };
        setDraft(empty);
        setApplied(empty);
        setPage(1);
    };

    const openTransaction = (tx) => {
        setRecords({ data: [], total: 0 });
        setRecPage(1);
        setSelected(tx);
    };

    // Row/record view passes the transaction id; the list passes the active filters
    const reportParams = (id) => id
        ? { type: "report", id }
        : { type: "report", module: applied.module, date_from: applied.date_from, date_to: applied.date_to };

    // ── Per-transaction records view ──
    if (selected) {
        const recordRows = records.data.map((r, i) => ({
            id: i,
            row_num: r.row_num,
            parent_id: r.parent_id,
            child_id: r.child_id,
            action_type: r.action_type,
            amount: r.amount ?? "",
            currency: r.currency ?? "",
            previous_status: r.previous_status ?? "",
            new_status: r.new_status ?? "",
            processing_status: r.processing_status,
            error_message: r.error_message ?? "",
            processing_datetime: formatUtc(r.processing_datetime)
        }));
        const columns = [
            { id: "row_num", label: "Row", noWrap: true },
            { id: "parent_id", label: "Parent ID", noWrap: true },
            { id: "child_id", label: "Child ID", noWrap: true },
            { id: "action_type", label: "Action", noWrap: true },
            ...(selected.module === "cost"
                ? [{ id: "amount", label: "Amount", noWrap: true }, { id: "currency", label: "Currency", noWrap: true }]
                : []),
            { id: "previous_status", label: "Previous Status", noWrap: true },
            { id: "new_status", label: "New Status", noWrap: true },
            { id: "processing_status", label: "Result", noWrap: true, tags: RECORD_TAGS },
            { id: "error_message", label: "Message", width: "300px" },
            { id: "processing_datetime", label: "Processed At", noWrap: true }
        ];
        return (
            <div>
                <div className="bulk-toolbar">
                    <McButton label="← Back to history" appearance="secondary" fit="small" onClick={() => setSelected(null)} />
                    <div className="bulk-toolbar-actions">
                        <McButton label={busy === "report" ? "Preparing..." : "Download report"} appearance="primary" fit="small" disabled={Boolean(busy)}
                            onClick={() => download("report", reportParams(selectedId), `${selectedId}_report.xlsx`)} />
                    </div>
                </div>

                <div className="bulk-summary-grid">
                    <SummaryCard tone="total" value={selected.total_records} label="Total Records" />
                    <SummaryCard tone="success" value={selected.success_count} label="Successful" />
                    <SummaryCard tone="error" value={selected.failure_count} label="Failed" />
                    <SummaryCard tone="skipped" value={selected.skipped_count} label="Skipped" />
                </div>

                <div className="bulk-table-wrapper">
                    <h4 className="bulk-card-title" style={{ marginBottom: "4px" }}>
                        {selectedId} · {MODULES[selected.module]?.label || selected.module}
                    </h4>
                    <p className="bulk-card-desc" style={{ marginBottom: "16px" }}>
                        {selected.file_name} · uploaded by {selected.uploaded_by} · <Pill map={TX_TAGS} value={selected.status} />
                    </p>
                    {isStale(selected) && (
                        <div className="bulk-alert bulk-alert--warning">
                            This transaction has been In Progress for more than {STALE_MINUTES} minutes. Please contact support.
                        </div>
                    )}
                    {recLoading && !records.data.length
                        ? <div className="bulk-progress-initial"><div className="loader"></div><p className="bulk-progress-text">Loading records...</p></div>
                        : <DataTable columns={columns} rows={recordRows} tableKey={`${selectedId}-${recPage}`} />}
                    <Pager page={recPage} pageSize={RECORDS_PAGE_SIZE} total={records.total} onChange={setRecPage} />
                </div>
            </div>
        );
    }

    // ── Transaction list view ──
    return (
        <div>
            <div className="bulk-card bulk-card--wide bulk-filter-card">
                <div className="bulk-filter-bar">
                    <div className="bulk-filter-field">
                        <McSelect
                            label="Module"
                            fit="small"
                            width="100"
                            value={draft.module || "all"}
                            optionselected={(e) => {
                                const v = e.detail?.value ?? e.detail;
                                setDraft((d) => ({ ...d, module: v === "all" ? "" : v }));
                            }}
                        >
                            <McOption value="all">All</McOption>
                            <McOption value="event">Event</McOption>
                            <McOption value="cost">Cost</McOption>
                        </McSelect>
                    </div>
                    <div className="bulk-filter-field">
                        <McInputDate
                            label="From"
                            fit="small"
                            width="100"
                            format="YYYY-MM-DD"
                            placeholder="YYYY-MM-DD"
                            clearbutton
                            autocomplete="off"
                            value={draft.date_from}
                            input={(e) => handleDateInput("date_from", e.target.value)}
                            inputdateselected={(e) => handleDateInput("date_from", e.detail)}
                            clearbuttonclick={() => handleDateInput("date_from", "")}
                        />
                    </div>
                    <div className="bulk-filter-field">
                        <McInputDate
                            label="To"
                            fit="small"
                            width="100"
                            format="YYYY-MM-DD"
                            placeholder="YYYY-MM-DD"
                            clearbutton
                            autocomplete="off"
                            value={draft.date_to}
                            input={(e) => handleDateInput("date_to", e.target.value)}
                            inputdateselected={(e) => handleDateInput("date_to", e.detail)}
                            clearbuttonclick={() => handleDateInput("date_to", "")}
                        />
                    </div>
                    <div className="bulk-toolbar-actions">
                        <McButton label="Apply" appearance="primary" fit="small" onClick={applyFilters} />
                        <McButton label="Reset" appearance="secondary" fit="small" onClick={resetFilters} />
                        <McButton label="Refresh" appearance="secondary" fit="small" onClick={() => setReload((n) => n + 1)} />
                    </div>
                </div>
                <div className="bulk-filter-reports">
                    <span className="bulk-filter-reports-label">
                        One workbook with a Summary sheet (per transaction) and a Details sheet (per record), for the applied filters
                    </span>
                    <McButton label={busy === "report" ? "Preparing..." : "Download report"} appearance="secondary" fit="small" disabled={Boolean(busy)}
                        onClick={() => download("report", reportParams(), "bulk_approval_report.xlsx")} />
                </div>
            </div>

            <div className="bulk-table-wrapper">
                <h4 className="bulk-card-title" style={{ marginBottom: "16px" }}>Bulk Transactions</h4>
                {loading && !list.data.length ? (
                    <div className="bulk-progress-initial"><div className="loader"></div><p className="bulk-progress-text">Loading history...</p></div>
                ) : !list.data.length ? (
                    <p className="bulk-card-desc">No bulk approval transactions found.</p>
                ) : (
                    <div className="bulk-html-table-wrap">
                        <table className="bulk-html-table">
                            <thead>
                                <tr>
                                    <th>Transaction</th><th>Module</th><th>File</th><th>Uploaded by</th>
                                    <th className="num">Total</th><th className="num">Success</th><th className="num">Failed</th><th className="num">Skipped</th>
                                    <th>Status</th><th>Started</th><th>Completed</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.data.map((tx) => (
                                    <tr key={tx.bulk_transaction_id}>
                                        <td><strong>{tx.bulk_transaction_id}</strong></td>
                                        <td>{MODULES[tx.module]?.label || tx.module}</td>
                                        <td className="wrap">{tx.file_name}</td>
                                        <td>{tx.uploaded_by}</td>
                                        <td className="num">{tx.total_records}</td>
                                        <td className="num">{tx.success_count}</td>
                                        <td className="num">{tx.failure_count}</td>
                                        <td className="num">{tx.skipped_count}</td>
                                        <td>
                                            <Pill map={TX_TAGS} value={tx.status} />
                                            {isStale(tx) && <div className="bulk-stale-hint">Contact support</div>}
                                        </td>
                                        <td>{formatUtc(tx.started_at)}</td>
                                        <td>{formatUtc(tx.completed_at)}</td>
                                        <td className="actions">
                                            <button type="button" className="bulk-link-btn" onClick={() => openTransaction(tx)}>Records</button>
                                            <button type="button" className="bulk-link-btn" disabled={Boolean(busy)}
                                                onClick={() => download(`r-${tx.bulk_transaction_id}`, reportParams(tx.bulk_transaction_id), `${tx.bulk_transaction_id}_report.xlsx`)}>Download report</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pager page={page} pageSize={HISTORY_PAGE_SIZE} total={list.total} onChange={setPage} />
            </div>
        </div>
    );
}

// ── Main panel ────────────────────────────────────────────────────
function BulkApproval({ token, showToast }) {
    const [view, setView]               = useState("new");          // "new" | "history"
    const [step, setStep]               = useState("upload");       // upload | preview | processing | result
    const [moduleId, setModuleId]       = useState("event");
    const [file, setFile]               = useState(null);
    const [validating, setValidating]   = useState(false);
    const [issues, setIssues]           = useState(null);           // { message, errors[], retry }
    const [validation, setValidation]   = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [progress, setProgress]       = useState(null);
    const [recovering, setRecovering]   = useState(false);
    const [result, setResult]           = useState(null);

    const fileRef       = useRef(null);
    const confirmingRef = useRef(false);
    const mountedRef    = useRef(true);
    const toastRef      = useRef(showToast);
    toastRef.current    = showToast;

    const { download, busy } = useDownloader(token, toastRef);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const resetFlow = () => {
        confirmingRef.current = false;
        fileRef.current = null;
        setFile(null);
        setIssues(null);
        setValidation(null);
        setProgress(null);
        setResult(null);
        setRecovering(false);
        setConfirmOpen(false);
        setStatusFilter("all");
        setStep("upload");
    };

    const handleModuleChange = (id) => {
        setModuleId(id);
        setIssues(null);
    };

    const handleFileChange = (e) => {
        const f = e?.detail?.[0] || null;
        fileRef.current = f;
        setFile(f);
        setIssues(null);
    };

    const validateDisabledReason = validating
        ? "Validation in progress..."
        : !file
        ? "Select a .xlsx file above to enable validation."
        : "";

    // ── Validate ──────────────────────────────────────────────────
    const handleValidate = async () => {
        const f = fileRef.current;
        if (!f) { showToast("Please select an Excel file before validating", "error"); return; }
        if (!/\.xlsx$/i.test(f.name)) { setIssues({ message: "Only .xlsx files are accepted", errors: [] }); return; }
        if (f.size > MAX_FILE_BYTES) { setIssues({ message: "File is larger than 5 MB", errors: [] }); return; }

        const formData = new FormData();
        formData.append("file", f);
        setValidating(true);
        setIssues(null);
        try {
            const res = await fetch(`${BASE}/validate?module=${moduleId}`, {
                method: "POST",
                headers: authHeaders(token),
                body: formData
            });
            if (!res.ok) {
                const err = await readError(res);
                setIssues({ message: err.message, errors: err.errors, retry: err.status === 502 });
                showToast(err.message, "error");
                return;
            }
            const data = await res.json();
            confirmingRef.current = false;
            setValidation(data);
            setStatusFilter("all");
            setStep("preview");
        } catch (e) {
            setIssues({ message: e.message || "Validation failed", errors: [], retry: true });
            showToast(e.message || "Validation failed", "error");
        } finally {
            setValidating(false);
        }
    };

    // ── Confirm → SSE ─────────────────────────────────────────────
    const finish = (data) => {
        if (!mountedRef.current) return;
        setResult(data);
        setProgress(null);
        setRecovering(false);
        setStep("result");
        if (data.status === "Completed" && !data.failure_count && !data.skipped_count) {
            toastRef.current(`Processed ${data.success_count} record(s). A summary email will be sent to you.`, "success");
        } else if (data.status === "Failed" || data.status === "Interrupted") {
            toastRef.current(`Processing ${data.status.toLowerCase()}`, "error");
        } else {
            toastRef.current(`Completed: ${data.success_count} success, ${data.failure_count} failed, ${data.skipped_count} skipped. A summary email will be sent to you.`, "warning");
        }
    };

    // The stream dropped without a "complete" event; processing continues server-side
    const recoverFromHistory = async (module, btId, startedAt, lastProgress) => {
        setRecovering(true);
        const began = Date.now();
        while (mountedRef.current && Date.now() - began < POLL_CEILING_MS) {
            try {
                const res = await fetch(`${BASE}/history?${buildQuery({ module, page_size: 5 })}`, { headers: authHeaders(token) });
                if (res.ok) {
                    const body = await res.json();
                    const txs = body.data || [];
                    const tx = btId
                        ? txs.find((t) => t.bulk_transaction_id === btId)
                        : txs.find((t) => {
                            const s = parseUtc(t.started_at);
                            return s && s.getTime() >= startedAt - 2 * 60 * 1000;
                        });
                    if (tx && tx.status !== "In Progress") {
                        finish({
                            bulk_transaction_id: tx.bulk_transaction_id,
                            module: tx.module,
                            status: tx.status,
                            total_records: tx.total_records,
                            success_count: tx.success_count,
                            failure_count: tx.failure_count,
                            skipped_count: tx.skipped_count
                        });
                        return;
                    }
                }
            } catch { /* keep polling */ }
            await sleep(POLL_MS);
        }
        if (!mountedRef.current) return;
        setRecovering(false);
        setProgress(null);
        setResult({
            bulk_transaction_id: btId,
            module,
            status: "In Progress",
            total_records: validation?.total_records ?? 0,
            success_count: lastProgress?.success_count ?? 0,
            failure_count: lastProgress?.error_count ?? 0,
            skipped_count: lastProgress?.skipped_count ?? 0
        });
        setStep("result");
    };

    const handleConfirm = async () => {
        if (confirmingRef.current || !validation) return;
        confirmingRef.current = true;
        setConfirmOpen(false);
        setProgress(null);
        setRecovering(false);
        setStep("processing");

        const startedAt = Date.now();
        const module = validation.module;
        let res;
        try {
            res = await fetch(`${BASE}/confirm`, {
                method: "POST",
                headers: { ...authHeaders(token), "Content-Type": "application/json" },
                body: JSON.stringify({ upload_id: validation.upload_id, allow_partial: true })
            });
        } catch (e) {
            // Request never reached the server; the upload is still usable
            confirmingRef.current = false;
            setStep("preview");
            showToast(e.message || "Could not reach the server. Please try again.", "error");
            return;
        }

        const isStream = (res.headers.get("content-type") || "").includes("text/event-stream");
        if (!res.ok || !isStream) {
            const err = await readError(res);
            // Upload unusable (already processed / not found / expired / other user's): validate again
            const spent = [403, 404, 409, 410].includes(err.status) || (err.status === 400 && err.hasDetail);
            if (spent) {
                showToast(err.status === 409 ? err.message : `${err.message}. Please validate the file again.`, "error");
                resetFlow();
            } else {
                confirmingRef.current = false;
                setStep("preview");
                showToast(err.message, "error");
            }
            return;
        }

        let btId = null;
        let completed = null;
        let lastProgress = null;
        const handleFrame = (frame) => {
            const evt = parseFrame(frame);
            if (!evt) return;
            if (evt.event === "progress") {
                const d = evt.data;
                btId = d.bulk_transaction_id || btId;
                lastProgress = d;
                if (mountedRef.current) setProgress(d);
            } else if (evt.event === "complete") {
                completed = evt.data;
            }
        };

        try {
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
                const frames = buffer.split("\n\n");
                buffer = frames.pop();
                frames.forEach(handleFrame);
            }
            if (buffer.trim()) handleFrame(buffer);
        } catch { /* stream dropped: fall through to history recovery */ }

        if (completed) finish(completed);
        else await recoverFromHistory(module, btId, startedAt, lastProgress);
    };

    // ── Derived values ────────────────────────────────────────────
    const modInfo = MODULES[validation?.module || moduleId];
    const previewRows = (validation?.rows || []).map((r, i) => ({
        id: i,
        row: r.row,
        parent_id: r.parent_id ?? "",
        child_id: r.child_id ?? "",
        action: r.action ?? "",
        amount: r.amount ?? "",
        currency: r.currency ?? "",
        status: r.status,
        message: r.message ?? "",
        previous_status: r.previous_status ?? "",
        new_status: r.new_status ?? ""
    }));
    const visibleRows = statusFilter === "all" ? previewRows : previewRows.filter((r) => r.status === statusFilter);
    const previewColumns = [
        { id: "row", label: "Row", noWrap: true },
        { id: "parent_id", label: "Parent ID", noWrap: true },
        { id: "child_id", label: "Child ID", noWrap: true },
        { id: "action", label: "Action", noWrap: true },
        ...(validation?.module === "cost"
            ? [{ id: "amount", label: "Amount", noWrap: true }, { id: "currency", label: "Currency", noWrap: true }]
            : []),
        { id: "status", label: "Status", noWrap: true, tags: ROW_TAGS },
        { id: "message", label: "Message", width: "300px" },
        { id: "previous_status", label: "Current Status", noWrap: true },
        { id: "new_status", label: "New Status", noWrap: true }
    ];
    const canConfirm = Boolean(validation) && validation.eligible_count > 0 && validation.can_proceed !== false;
    const pct = progress
        ? Math.min(100, Math.max(0, progress.percentage ?? (progress.total ? (progress.processed / progress.total) * 100 : 0)))
        : 0;

    // ── Render ────────────────────────────────────────────────────
    return (
        <div className="bulk-approval">
            <div className="bulk-subnav">
                <div className="bulk-segment">
                    <button type="button" className={`bulk-segment-btn${view === "new" ? " bulk-segment-btn--active" : ""}`}
                        onClick={() => setView("new")}>New Approval</button>
                    <button type="button" className={`bulk-segment-btn${view === "history" ? " bulk-segment-btn--active" : ""}`}
                        onClick={() => setView("history")}>History &amp; Reports</button>
                </div>
            </div>

            {view === "history" && <ApprovalHistory token={token} toastRef={toastRef} />}

            {view === "new" && step === "upload" && (
                <div className="bulk-upload-single">
                    <div className="bulk-card">
                        <h4 className="bulk-card-title">Upload Approval / Rejection Excel File</h4>
                        <p className="bulk-card-desc">
                            Approve or reject charges in bulk. Nothing is changed until you review the validation result and confirm.
                        </p>

                        <div className="bulk-module-row">
                            <ModuleToggle value={moduleId} onChange={handleModuleChange} disabled={validating} />
                            <McButton
                                label="Download Template"
                                appearance="secondary"
                                fit="small"
                                disabled={Boolean(busy)}
                                onClick={() => download("template", { type: "template", module: moduleId }, `bulk_${moduleId}_approval_template.xlsx`)}
                            />
                        </div>

                        <p className="bulk-card-desc">
                            File must contain the required columns: <strong>{MODULES[moduleId].columns}</strong>
                        </p>
                        <RulesList moduleId={moduleId} />

                        <div className="bulk-file-upload-area">
                            <McFileUpload label="Select Excel File" accept=".xlsx" input={handleFileChange} />
                        </div>

                        {file && (
                            <div className="bulk-file-info">
                                <div className="bulk-file-info-row">
                                    <span className="bulk-file-info-label">File</span>
                                    <span className="bulk-file-info-value">{file.name}</span>
                                </div>
                                <div className="bulk-file-info-row">
                                    <span className="bulk-file-info-label">Size</span>
                                    <span className="bulk-file-info-value">{(file.size / 1024).toFixed(1)} KB</span>
                                </div>
                            </div>
                        )}

                        {issues && (
                            <div className="bulk-alert bulk-alert--error">
                                <strong>{issues.message}</strong>
                                {issues.errors?.length > 0 && (
                                    <ul>{issues.errors.map((m, i) => <li key={i}>{m}</li>)}</ul>
                                )}
                                {issues.retry && <div>Click Validate File to try again.</div>}
                            </div>
                        )}

                        <div className="bulk-upload-action">
                            {validateDisabledReason && !validating && (
                                <span className="bulk-disabled-hint">{validateDisabledReason}</span>
                            )}
                            <McButton onClick={handleValidate} appearance="primary" disabled={!file || validating}>
                                {validating ? "Validating..." : "Validate File"}
                            </McButton>
                        </div>

                        {validating && (
                            <div className="bulk-progress">
                                <div className="bulk-progress-initial">
                                    <div className="loader"></div>
                                    <p className="bulk-progress-text">Validating file against current charge data...</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {view === "new" && step === "preview" && validation && (
                <div>
                    <div className="bulk-toolbar">
                        <div>
                            <h4 className="bulk-card-title" style={{ marginBottom: "2px" }}>Validation Preview</h4>
                            <p className="bulk-card-desc">
                                {validation.file_name} · {modInfo.label} module · {validation.approve_count} to approve, {validation.reject_count} to reject
                            </p>
                        </div>
                        <div className="bulk-toolbar-actions">
                            <McButton label="Download validation result" appearance="secondary" fit="small" disabled={Boolean(busy)}
                                onClick={() => download("validation", { type: "validation", id: validation.upload_id }, `validation_${validation.upload_id}.xlsx`)} />
                            <McButton label="Start over" appearance="secondary" fit="small" onClick={resetFlow} />
                            <McButton label="Confirm & Process" appearance="primary" fit="small" disabled={!canConfirm}
                                onClick={() => setConfirmOpen(true)} />
                        </div>
                    </div>

                    <div className="bulk-summary-grid">
                        <SummaryCard tone="total" value={validation.total_records} label="Total Records" />
                        <SummaryCard tone="success" value={validation.eligible_count} label="Eligible" />
                        <SummaryCard tone="error" value={validation.failed_count} label="Failed" />
                        <SummaryCard tone="skipped" value={validation.skipped_count} label="Skipped" />
                    </div>

                    {!canConfirm && (
                        <div className="bulk-alert bulk-alert--warning">
                            No eligible records, so nothing can be processed. Download the validation result to see why each row was rejected, fix the file, and upload it again.
                        </div>
                    )}

                    <div className="bulk-table-wrapper">
                        <div className="bulk-table-head">
                            <h4 className="bulk-card-title" style={{ margin: 0 }}>Row Validation</h4>
                            <div className="bulk-chips">
                                {[["all", "All"], ["valid", "Valid"], ["failed", "Failed"], ["skipped", "Skipped"]].map(([id, label]) => (
                                    <button key={id} type="button"
                                        className={`bulk-chip${statusFilter === id ? " bulk-chip--active" : ""}`}
                                        onClick={() => setStatusFilter(id)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <DataTable columns={previewColumns} rows={visibleRows} tableKey={`${validation.upload_id}-${statusFilter}`} />
                    </div>
                </div>
            )}

            {view === "new" && step === "processing" && (
                <div className="bulk-upload-single">
                    <div className="bulk-card">
                        <h4 className="bulk-card-title">Processing</h4>
                        <p className="bulk-card-desc">
                            Processing continues on the server even if you close this page.
                        </p>
                        <div className="bulk-progress">
                            {recovering ? (
                                <div className="bulk-progress-initial">
                                    <div className="loader"></div>
                                    <p className="bulk-progress-text">
                                        Connection lost. Processing is still running, checking its status...
                                    </p>
                                </div>
                            ) : progress ? (
                                <>
                                    <div className="bulk-progress-header">
                                        <span className="bulk-progress-pct">{Math.round(pct * 10) / 10}%</span>
                                        <span className="bulk-progress-counts">{progress.processed} / {progress.total} records</span>
                                    </div>
                                    <div className="bulk-progress-bar-track">
                                        <div className="bulk-progress-bar-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="bulk-progress-stats">
                                        <span className="bulk-progress-stat-ok">✓ {progress.success_count} success</span>
                                        {progress.error_count > 0 && <span className="bulk-progress-stat-err">✗ {progress.error_count} failed</span>}
                                        {progress.skipped_count > 0 && <span className="bulk-progress-stat-warn">– {progress.skipped_count} skipped</span>}
                                    </div>
                                    {progress.current_result && (
                                        <div className="bulk-progress-current">
                                            Row {progress.current_result.row} · {progress.current_result.child_id} · {progress.current_result.action} ·{" "}
                                            <Pill map={RECORD_TAGS} value={progress.current_result.status} />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="bulk-progress-initial">
                                    <div className="loader"></div>
                                    <p className="bulk-progress-text">Re-validating and starting processing...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {view === "new" && step === "result" && result && (
                <div>
                    <div className="bulk-toolbar">
                        <div>
                            <h4 className="bulk-card-title" style={{ marginBottom: "2px" }}>Result Summary</h4>
                            <p className="bulk-card-desc">
                                {result.bulk_transaction_id || "Transaction"} · {MODULES[result.module]?.label || result.module} module ·{" "}
                                <Pill map={TX_TAGS} value={result.status} />
                            </p>
                        </div>
                        <div className="bulk-toolbar-actions">
                            <McButton label={busy === "report" ? "Preparing..." : "Download report"} appearance="secondary" fit="small"
                                disabled={Boolean(busy) || !result.bulk_transaction_id}
                                onClick={() => download("report", { type: "report", id: result.bulk_transaction_id }, `${result.bulk_transaction_id}_report.xlsx`)} />
                            <McButton label="View history" appearance="secondary" fit="small" onClick={() => setView("history")} />
                            <McButton label="New Upload" appearance="primary" fit="small" onClick={resetFlow} />
                        </div>
                    </div>

                    {result.status === "In Progress" && (
                        <div className="bulk-alert bulk-alert--warning">
                            We lost the connection and this transaction is still In Progress. Check History &amp; Reports for the final result.
                            If it stays In Progress for more than {STALE_MINUTES} minutes, please contact support.
                        </div>
                    )}
                    {result.status === "Interrupted" && (
                        <div className="bulk-alert bulk-alert--warning">
                            Processing was interrupted on the server. Records processed before the interruption are listed in the report (Details sheet, Processing Status = Success).
                        </div>
                    )}
                    {result.status === "Failed" && (
                        <div className="bulk-alert bulk-alert--error">
                            No records were processed successfully. Download the report and check the Details sheet for the reasons.
                        </div>
                    )}

                    <div className="bulk-summary-grid">
                        <SummaryCard tone="total" value={result.total_records} label="Total Records" />
                        <SummaryCard tone="success" value={result.success_count} label="Successful" />
                        <SummaryCard tone="error" value={result.failure_count} label="Failed" />
                        <SummaryCard tone="skipped" value={result.skipped_count} label="Skipped" />
                    </div>
                    <p className="bulk-card-desc">
                        Counts come from the final run, which re-validates the file against live data, so they can differ slightly from the preview.
                    </p>
                </div>
            )}

            {confirmOpen && validation && (
                <div className="bulk-modal-backdrop" role="presentation">
                    <div className="bulk-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-confirm-title">
                        <h4 id="bulk-confirm-title" className="bulk-card-title">Confirm bulk {modInfo.label.toLowerCase()} processing</h4>
                        <p className="bulk-card-desc">
                            You are about to process <strong>{validation.eligible_count}</strong> record(s):{" "}
                            <strong>{validation.approve_count}</strong> approve, <strong>{validation.reject_count}</strong> reject.
                        </p>
                        {(validation.failed_count > 0 || validation.skipped_count > 0) && (
                            <p className="bulk-card-desc" style={{ marginTop: "8px" }}>
                                {validation.failed_count} failed and {validation.skipped_count} skipped row(s) will not be changed.
                            </p>
                        )}
                        <p className="bulk-card-desc" style={{ marginTop: "8px" }}>
                            This updates Salesforce and cannot be undone. The file is re-validated first, so final counts may differ.
                        </p>
                        <div className="bulk-modal-actions">
                            <McButton label="Cancel" appearance="secondary" fit="small" onClick={() => setConfirmOpen(false)} />
                            <McButton label="Confirm & Process" appearance="primary" fit="small" onClick={handleConfirm} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BulkApproval;
